
import { toast } from 'sonner';
import { logger } from '@/services/LoggingService';
import { ConnectionProcessingState } from '../constants';
import { VezgoError } from '../types';

interface ErrorHandlerOptions {
  provider: string;
  operation: string;
  showToast?: boolean;
  setProcessingState?: (state: ConnectionProcessingState) => void;
  toastDuration?: number;
}

/**
 * Standardized error handler for Vezgo integration
 * Provides consistent logging, user notification, and state updates
 * 
 * @param error The error to handle
 * @param options Configuration options for error handling
 * @returns Boolean indicating if the error was handled
 */
export function handleVezgoError(error: any, options: ErrorHandlerOptions): boolean {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const vezgoError = errorObj as VezgoError;
  const errorCode = vezgoError.code || 'UNKNOWN';
  const errorMessage = vezgoError.message || 'An unknown error occurred';
  
  // Log the error with standardized format
  logger.error('vezgo:error-handler', `Error during ${options.operation}`, {
    provider: options.provider,
    errorCode,
    errorMessage,
    operation: options.operation,
    timestamp: new Date().toISOString(),
    errorDetails: vezgoError
  });
  
  // Update state if state setter provided
  if (options.setProcessingState) {
    options.setProcessingState(ConnectionProcessingState.ERROR);
    
    logger.debug('vezgo:error-handler', 'Updated processing state to ERROR', {
      provider: options.provider,
      timestamp: new Date().toISOString()
    });
  }
  
  // Show toast notification if requested
  if (options.showToast) {
    const toastMessage = vezgoError.userMessage || `Error connecting to ${options.provider}: ${errorMessage}`;
    
    toast.error(toastMessage, {
      duration: options.toastDuration || 5000,
      position: 'top-center'
    });
    
    logger.debug('vezgo:error-handler', 'Displayed error toast', {
      provider: options.provider,
      message: toastMessage,
      timestamp: new Date().toISOString()
    });
  }
  
  return true;
}

/**
 * Detects session expiry errors and handles them appropriately
 * Session expiry requires special handling as it often needs reauthorization
 * 
 * @param error The error to check for session expiry
 * @param provider Provider name for context
 * @returns Boolean indicating if error was a session expiry
 */
export function detectSessionExpiry(error: any, provider?: string): boolean {
  if (!error) return false;
  
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const errorMessage = errorObj.message.toLowerCase();
  const errorCode = (errorObj as VezgoError).code || '';
  
  // Common session expiry patterns
  const sessionExpired = 
    errorCode === 'UNAUTHORIZED' || 
    errorCode === 'SESSION_EXPIRED' ||
    errorCode === '401' ||
    errorMessage.includes('unauthorized') ||
    errorMessage.includes('session expired') ||
    errorMessage.includes('token expired') ||
    errorMessage.includes('not authenticated');
  
  if (sessionExpired) {
    logger.warn('vezgo:error-handler', 'Detected session expiry', {
      provider,
      errorCode,
      errorMessage
    });
    
    toast.error(`Your session with ${provider || 'the provider'} has expired. Please reconnect.`, {
      duration: 7000,
      position: 'top-center'
    });
    
    return true;
  }
  
  return false;
}

/**
 * Categorizes errors into different types for targeted handling
 * 
 * @param error The error to categorize
 * @returns Object with error category and properties
 */
export function categorizeVezgoError(error: any) {
  if (!error) return { category: 'none', recoverable: true };
  
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const errorMessage = errorObj.message.toLowerCase();
  const errorCode = (errorObj as VezgoError).code || '';
  
  // Authentication errors
  if (
    errorCode === 'UNAUTHORIZED' || 
    errorCode === '401' ||
    errorMessage.includes('unauthorized') ||
    errorMessage.includes('authentication') ||
    errorMessage.includes('token')
  ) {
    return {
      category: 'auth',
      recoverable: true,
      requiresReconnect: true,
      message: 'Authentication error. Please reconnect.'
    };
  }
  
  // Connection errors
  if (
    errorCode === 'CONNECTION_ERROR' ||
    errorCode === 'NETWORK_ERROR' ||
    errorMessage.includes('network') ||
    errorMessage.includes('connect') ||
    errorMessage.includes('timeout')
  ) {
    return {
      category: 'connection',
      recoverable: true,
      requiresReconnect: false,
      message: 'Connection issue. Please try again.'
    };
  }
  
  // API errors
  if (
    errorCode === 'API_ERROR' ||
    errorCode === 'RATE_LIMIT' ||
    errorCode === '429' ||
    errorMessage.includes('api') ||
    errorMessage.includes('rate limit') ||
    errorMessage.includes('too many requests')
  ) {
    return {
      category: 'api',
      recoverable: true,
      requiresReconnect: false,
      message: 'API error. Please try again later.'
    };
  }
  
  // Provider errors
  if (
    errorCode === 'PROVIDER_ERROR' ||
    errorMessage.includes('provider')
  ) {
    return {
      category: 'provider',
      recoverable: false,
      requiresReconnect: true,
      message: 'Provider error. Please try again later.'
    };
  }
  
  // Default - unknown error
  return {
    category: 'unknown',
    recoverable: true,
    requiresReconnect: false,
    message: 'An error occurred. Please try again.'
  };
}

/**
 * Creates a user-friendly error message based on error details
 * 
 * @param error The error object
 * @param provider Provider name for context
 * @returns User-friendly error message
 */
export function getUserFriendlyErrorMessage(error: any, provider?: string): string {
  const errorCategory = categorizeVezgoError(error);
  const providerName = provider || 'the provider';
  
  switch (errorCategory.category) {
    case 'auth':
      return `Your session with ${providerName} has expired. Please reconnect.`;
    
    case 'connection':
      return `Unable to connect to ${providerName}. Please check your internet connection and try again.`;
    
    case 'api':
      return `${providerName} service is temporarily unavailable. Please try again later.`;
    
    case 'provider':
      return `There was an issue with ${providerName}. Please try again later.`;
    
    case 'unknown':
    default:
      return `An error occurred while connecting to ${providerName}. Please try again.`;
  }
}

/**
 * Formats error messages for display
 * 
 * @param error The error to format
 * @returns Formatted error message string
 */
export function formatErrorMessage(error: any): string {
  if (!error) return 'Unknown error';
  
  // Handle string errors
  if (typeof error === 'string') return error;
  
  // Handle Error objects
  if (error instanceof Error) return error.message;
  
  // Handle VezgoError objects with user-friendly messages
  if (typeof error === 'object' && error !== null) {
    if ('userMessage' in error && error.userMessage) return error.userMessage;
    if ('message' in error && error.message) return String(error.message);
    if ('code' in error && error.code) return `Error code: ${error.code}`;
  }
  
  // Fallback for any other error type
  return String(error);
}
